import {
  Alert,
  Button,
  Loader,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { useAvailableOrganizations } from "../auth/useAvailableOrganizations";
import { useMyPermissions } from "../permissions/useMyPermissions";
import { useAuthStore } from "../../auth/store";
import { serverErrorText } from "./serverError";
import { useRenameOrganization } from "./useRenameOrganization";

// Право на смену имени организации (владелец).
const PROFILE_MANAGE = "org.profile.manage";

// Предел длины имени — ширина колонки `organization.name`.
const NAME_MAX_LENGTH = 255;

// Секция имени: название редактируется, slug показывается только для
// чтения. Источник — список организаций пользователя (`/me/organizations`):
// отдельной ручки чтения нет.
export function OrganizationNameSection(): ReactElement {
  const { t } = useTranslation();
  const orgId = useAuthStore((state) => state.identity?.orgId ?? null);
  const organizations = useAvailableOrganizations();

  if (organizations.isPending) {
    return <Loader />;
  }
  const org = organizations.data?.find((item) => item.org_id === orgId);
  if (organizations.isError || org === undefined) {
    return <Alert color="red" title={t("settings.errorHeading")} />;
  }
  // `key` по сохранённому имени: после успеха список перечитывается →
  // тело перемонтируется и черновик берёт новое имя.
  return (
    <OrganizationNameBody key={org.name} name={org.name} slug={org.slug} />
  );
}

function OrganizationNameBody({
  name,
  slug,
}: {
  readonly name: string;
  readonly slug: string;
}): ReactElement {
  const { t } = useTranslation();
  const permissions = useMyPermissions();
  const rename = useRenameOrganization();
  const [draft, setDraft] = useState(name);

  const readOnly = permissions.isDenied(PROFILE_MANAGE);
  const trimmed = draft.trim();
  // Длину считаем в кодовых точках, как сервер, а не в UTF-16 единицах.
  const length = [...trimmed].length;
  const submittable =
    length > 0 && length <= NAME_MAX_LENGTH && trimmed !== name;

  return (
    <Stack gap="md">
      <Title order={2}>{t("settings.name.title")}</Title>
      <Text c="dimmed">
        {t("settings.orgSlugLabel")}: {slug}
      </Text>
      <TextInput
        label={t("settings.orgNameLabel")}
        value={draft}
        readOnly={readOnly}
        description={readOnly ? t("settings.name.readOnly") : undefined}
        error={
          rename.error !== null ? serverErrorText(rename.error, t) : undefined
        }
        onChange={(event) => {
          setDraft(event.currentTarget.value);
        }}
      />
      {readOnly ? null : (
        <div>
          <Button
            onClick={() => {
              rename.mutate({ name: trimmed });
            }}
            disabled={!submittable}
            loading={rename.isPending}
          >
            {t("settings.name.saveButton")}
          </Button>
        </div>
      )}
    </Stack>
  );
}
